import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DeviceType } from "@/components/filters/DeviceTypeFilter";
import { CoreService } from "@/services/core/CoreService";
import type { RequestReportItem } from "@/services/features/AnalyticsService";
import { AnalyticsServiceV2 } from "@/services/features/AnalyticsServiceV2";
import InventoryService, {
  type InventoryItem,
} from "@/services/features/inventory/InventoryService";
import { DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import { Store } from "@/types/store";
import { getBrowserTimezone } from "@/utils/storeTime";

export interface MissedOccurrence {
  id: number;
  time: string;
  storeId: number;
  imageUrl: string;
  productName: string;
  sku: string;
  size: string;
  color: string;
  storeName: string;
  storeNumber: string;
  associateName: string;
  searchTimeSeconds: number;
  value?: number;
  createdAt: number;
  assignedAt?: number;
  completedAt?: number;
  type: string;
  isAssociateRequest: boolean;
}

const formatAssociateName = (firstName?: string | null, lastName?: string | null): string => {
  const safeFirstName = firstName?.trim();
  const safeLastName = lastName?.trim();

  if (safeFirstName && safeLastName) {
    return `${safeFirstName} ${safeLastName.charAt(0)}.`;
  }

  if (safeFirstName) {
    return safeFirstName;
  }

  if (safeLastName) {
    return safeLastName;
  }

  return "Unassigned";
};

const buildInventoryCacheKey = (storeId: number, sku: string): string => `${storeId}:${sku}`;

const isAssociateRequestType = (type?: string): boolean =>
  type === "ASSOCIATE" || type === "DOOR_UNLOCK";

export const useMissedOccurrences = (
  stores: Store[] | null,
  dateRange: DateRange,
  deviceType: DeviceType,
) => {
  const browserTimezone = getBrowserTimezone();
  const [data, setData] = useState<MissedOccurrence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);
  const inventoryServiceRef = useRef<InventoryService | null>(null);
  const inventoryImageCacheRef = useRef<Map<string, string>>(new Map());
  const pendingInventoryRequestsRef = useRef<Set<string>>(new Set());
  const activeRequestIdRef = useRef(0);
  const storesRef = useRef(stores);
  storesRef.current = stores;
  const storeIdsKey = stores?.map((store) => store.id).join(",") ?? "";
  const storeIds = useMemo(
    () => (storeIdsKey ? storeIdsKey.split(",").map(Number) : []),
    [storeIdsKey],
  );
  const startDate = storePerformanceService.formatDate(dateRange?.start, browserTimezone);
  const endDate = storePerformanceService.formatDate(dateRange?.end, browserTimezone);
  const hasRequestInputs = Boolean(storeIdsKey && startDate && endDate);
  const requestKey = hasRequestInputs
    ? `${storeIdsKey}:${startDate}:${endDate}:${deviceType}`
    : null;

  const loadImageForOccurrence = useCallback((occurrence: MissedOccurrence) => {
    const inventoryService = inventoryServiceRef.current;

    if (!inventoryService || occurrence.imageUrl || !occurrence.sku || occurrence.sku === "--") {
      return;
    }

    const inventoryCacheKey = buildInventoryCacheKey(occurrence.storeId, occurrence.sku);
    const cachedImageUrl = inventoryImageCacheRef.current.get(inventoryCacheKey);

    if (cachedImageUrl !== undefined) {
      if (cachedImageUrl) {
        setData((previousData) => {
          let hasChanges = false;

          const nextData = previousData.map((item) => {
            if (
              item.storeId === occurrence.storeId &&
              item.sku === occurrence.sku &&
              item.imageUrl !== cachedImageUrl
            ) {
              hasChanges = true;
              return { ...item, imageUrl: cachedImageUrl };
            }

            return item;
          });

          return hasChanges ? nextData : previousData;
        });
      }

      return;
    }

    if (pendingInventoryRequestsRef.current.has(inventoryCacheKey)) {
      return;
    }

    const requestId = activeRequestIdRef.current;
    pendingInventoryRequestsRef.current.add(inventoryCacheKey);

    inventoryService
      .getSkuBySkuId(occurrence.storeId, occurrence.sku)
      .then((skuData: InventoryItem) => {
        if (activeRequestIdRef.current !== requestId) {
          return;
        }

        const imageUrl = skuData?.imageUrl || "";
        inventoryImageCacheRef.current.set(inventoryCacheKey, imageUrl);

        if (!imageUrl) {
          return;
        }

        setData((previousData) => {
          let hasChanges = false;

          const nextData = previousData.map((item) => {
            if (
              item.storeId === occurrence.storeId &&
              item.sku === occurrence.sku &&
              item.imageUrl !== imageUrl
            ) {
              hasChanges = true;
              return { ...item, imageUrl };
            }

            return item;
          });

          return hasChanges ? nextData : previousData;
        });
      })
      .catch(() => {
        if (activeRequestIdRef.current === requestId) {
          inventoryImageCacheRef.current.set(inventoryCacheKey, "");
        }
      })
      .finally(() => {
        pendingInventoryRequestsRef.current.delete(inventoryCacheKey);
      });
  }, []);

  useEffect(() => {
    activeRequestIdRef.current += 1;
    inventoryImageCacheRef.current = new Map();
    pendingInventoryRequestsRef.current = new Set();

    if (!requestKey) {
      setData([]);
      setLoading(false);
      setError(null);
      setCompletedRequestKey(null);
      return;
    }

    const requestId = activeRequestIdRef.current;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const coreService = CoreService.getInstance();
        const token = coreService.getAuthorizationHeader();
        inventoryServiceRef.current = token ? new InventoryService(token) : null;
        const analyticsService = AnalyticsServiceV2.getInstance();

        const response = await analyticsService.getRequestsByStatus(
          storeIds,
          "MISSED",
          startDate,
          endDate,
        );

        const allRequests: RequestReportItem[] = response.data || [];

        // Device type mapping across endpoints is inconsistent; skip deviceType filtering here to avoid dropping valid rows.
        if (deviceType && deviceType !== "all") {
          console.log(
            `[useMissedOccurrences] Skipping deviceType filter (${deviceType}) to avoid excluding valid MISSED requests`,
          );
        }

        // Map stores for easy lookup
        const storeMap = new Map<number, Store>();
        storesRef.current?.forEach((store) => storeMap.set(store.id, store));

        // Enrich with product details
        const enrichedData: MissedOccurrence[] = [];
        for (const item of allRequests) {
          const req = item.request;
          const store = storeMap.get(req.storeId);
          const isAssociateRequest = isAssociateRequestType(req.type);

          const productName = isAssociateRequest ? "" : req.title || "Unknown Product";
          const size = isAssociateRequest ? "" : req.size || "--";

          const searchTime =
            req.completedAt && (req.assignedAt || req.createdAt)
              ? (req.completedAt - (req.assignedAt || req.createdAt)) / 1000
              : 0;

          const value = isAssociateRequest ? undefined : req.price || 0;

          enrichedData.push({
            id: req.id,
            time: new Date(req.createdAt).toISOString(),
            storeId: req.storeId,
            imageUrl: "",
            productName,
            sku: isAssociateRequest ? "" : req.sku || "--",
            size,
            color: isAssociateRequest ? "" : req.color || "--",
            storeName: store?.name || "Unknown Store",
            storeNumber: store?.id.toString() || "",
            associateName: item.user
              ? formatAssociateName(item.user.firstName, item.user.lastName)
              : "Unassigned",
            searchTimeSeconds: Math.max(0, Math.round(searchTime)),
            value,
            createdAt: req.createdAt,
            assignedAt: req.assignedAt,
            completedAt: req.completedAt,
            type: req.type || "UNKNOWN",
            isAssociateRequest,
          });
        }

        // Sort by Time descending (most recent first)
        enrichedData.sort((a, b) => b.createdAt - a.createdAt);

        if (cancelled || activeRequestIdRef.current !== requestId) {
          return;
        }

        setData(enrichedData);
      } catch (err) {
        if (cancelled || activeRequestIdRef.current !== requestId) {
          return;
        }

        console.error("Error fetching missed occurrences:", err);
        setError("Failed to load occurrences");
        setData([]);
      } finally {
        if (!cancelled && activeRequestIdRef.current === requestId) {
          setLoading(false);
          setCompletedRequestKey(requestKey);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [deviceType, endDate, requestKey, startDate, storeIds]);

  const isRequestPending = hasRequestInputs && completedRequestKey !== requestKey;

  return { data, loading: loading || isRequestPending, error, loadImageForOccurrence };
};
